// Finance hub — ONE tabbed surface for payment operations (gateway audit
// Phase 5, the sidebar-overload antidote): a Payments overview of the tenant's
// recent disbursement batches across ALL purposes, the reconciliation queue,
// and the statutory filing register. The old payroll/reconciliation and
// payroll/filings routes redirect here; ?tab= deep-links each tab.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2, Landmark, Send, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/features/shared/components/ui/tabs';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { payrollApi } from '../api/payrollApi';
import { formatMoney, formatDate } from '../lib/format';
import { DISBURSEMENT_BATCH_META } from '../lib/state';
import { StatusChip } from '../components/StatusChip';
import { StatCard } from '../components/StatCard';
import type { DisbursementBatch, DisbursementPurpose } from '../types';
import { ReconciliationQueue } from './ReconciliationPage';
import { FilingsRegister } from './FilingsPage';
import { AccountingPanel } from './AccountingPanel';
import { getAccountingStatus } from '@/services/accountingService';

const TAB_IDS = ['payments', 'reconciliation', 'filings', 'accounting'] as const;
type TabId = (typeof TAB_IDS)[number];

export const FinanceHubPage: React.FC = () => {
  const { t } = useTranslation('payroll');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canViewPayroll, canApprovePayroll, hasPermission } = useRoleAccess();
  // The accounting tab appears only where the QuickBooks connector actually
  // exists: an environment without Intuit credentials (every tenant today)
  // would otherwise carry a tab whose only content is "not available".
  const canReadAccounting = hasPermission('accounting:read');
  // The finance lead owns the MAPPING; connecting the company's books stays a
  // tenant-admin act, so the two are gated separately.
  const canMapAccounting = hasPermission('accounting:map');
  const canConnectAccounting = hasPermission('migration:admin');
  const [accountingAvailable, setAccountingAvailable] = useState(false);
  useEffect(() => {
    if (!canReadAccounting) return;
    // No company on purpose: this is the WORKSPACE question — does this
    // tenant get an accounting tab at all. Asking it per company would need
    // an entity the page has not chosen yet, and would answer the wrong
    // question anyway.
    getAccountingStatus('quickbooks')
      // `offered` is per-tenant; `configured` is per-deployment and showed this
      // tab to every tenant the moment production keys were set. An already
      // connected tenant keeps the tab regardless, so they can see state and
      // disconnect even if an operator later closes the catalog row.
      .then((st) => setAccountingAvailable(Boolean(st.offered) || st.connected))
      .catch(() => setAccountingAvailable(false));
  }, [canReadAccounting]);

  // Tab state lives in the URL (?tab=…) so redirects/links can deep-link.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') as TabId | null;
  const tabAllowed = (id: TabId | null): boolean => {
    if (!id || !TAB_IDS.includes(id)) return false;
    if (id === 'reconciliation') return canApprovePayroll;
    if (id === 'accounting') return accountingAvailable && canReadAccounting;
    return true;
  };
  const tab: TabId = tabAllowed(requested) ? (requested as TabId) : 'payments';
  const setTab = (v: string) => setSearchParams({ tab: v }, { replace: true });

  // Optional ?year=&month= deep-links the Filings register to a run's period.
  const yearParam = Number(searchParams.get('year'));
  const monthParam = Number(searchParams.get('month'));
  const filingsInitialPeriod =
    yearParam >= 2000 && monthParam >= 1 && monthParam <= 12
      ? { year: yearParam, month: monthParam }
      : null;

  // Recent batches (Payments tab + the stat cards).
  const [batches, setBatches] = useState<DisbursementBatch[]>([]);
  const [loading, setLoading] = useState(true);

  // Header badges: reconciliation queue size (red) + overdue filings (amber).
  // Seeded here on mount; kept live by the tab bodies via the callbacks below.
  const [reconCount, setReconCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const onReconCountChange = useCallback((n: number) => setReconCount(n), []);
  const onOverdueChange = useCallback((n: number) => setOverdueCount(n), []);

  useEffect(() => {
    payrollApi
      .getRecentBatches(25)
      .then(setBatches)
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
    if (canApprovePayroll) {
      payrollApi
        .getReconciliationQueue()
        .then((items) => setReconCount(items.length))
        .catch(() => {});
    }
    payrollApi
      .getFilingRegister()
      .then((reg) =>
        setOverdueCount(
          (reg.outstanding ?? []).filter((o) => o.overdue).length +
            (reg.filings ?? []).filter((f) => f.overdue).length,
        ),
      )
      .catch(() => {});
  }, [canApprovePayroll]);

  const stats = useMemo(
    () => ({
      inFlight: batches.filter((b) => b.status === 'prepared' || b.status === 'submitted').length,
      completed: batches.filter((b) => b.status === 'completed').length,
      failed: batches.filter((b) => b.status === 'failed').length,
    }),
    [batches],
  );

  const purposeLabel = (p?: DisbursementPurpose): string => {
    switch (p) {
      case 'loan_advance':
        return t('financeHub.purposeLoan', { defaultValue: 'Advance payout' });
      case 'terminal_dues':
        return t('financeHub.purposeSettlement', { defaultValue: 'Terminal dues' });
      default:
        return t('financeHub.purposeNetPay', { defaultValue: 'Net pay' });
    }
  };

  // The hub lives at …/payroll/finance — sibling payroll pages share the prefix.
  const base = pathname.replace(/\/finance\/?$/, '');
  const openBatch = (b: DisbursementBatch) => {
    if (b.purpose === 'loan_advance') navigate(`${base}/loans`);
    else if (b.purpose === 'terminal_dues') navigate(`${base}/final-settlement`);
    else if (b.runId) navigate(`${base}/runs/${b.runId}?tab=disbursements`);
  };

  if (!canViewPayroll) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        {t('financeHub.noAccess', { defaultValue: 'You need payroll access to view finance operations.' })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header: title + attention badges */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-500/10 p-2 text-primary-500">
            <Landmark className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {t('financeHub.title', { defaultValue: 'Finance & Filings' })}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('financeHub.description', {
                defaultValue: 'Payments, reconciliation and statutory filings in one place.',
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canApprovePayroll && reconCount > 0 && (
            <Badge variant="danger">
              {t('financeHub.reconBadge', {
                defaultValue: '{{count}} to reconcile',
                count: reconCount,
              })}
            </Badge>
          )}
          {overdueCount > 0 && (
            <Badge variant="warning">
              {t('financeHub.overdueBadge', {
                defaultValue: '{{count}} filing(s) overdue',
                count: overdueCount,
              })}
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="payments">
            {t('financeHub.tabPayments', { defaultValue: 'Payments' })}
          </TabsTrigger>
          {canApprovePayroll && (
            <TabsTrigger value="reconciliation">
              {t('financeHub.tabReconciliation', { defaultValue: 'Reconciliation' })}
            </TabsTrigger>
          )}
          <TabsTrigger value="filings">
            {t('financeHub.tabFilings', { defaultValue: 'Filings' })}
          </TabsTrigger>
          {accountingAvailable && canReadAccounting && (
            <TabsTrigger value="accounting">
              {t('financeHub.tabAccounting', { defaultValue: 'Accounting' })}
            </TabsTrigger>
          )}
        </TabsList>

        {/* --- Payments: recent batches across all purposes ------------------ */}
        <TabsContent value="payments" className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={Send}
              tone="info"
              label={t('financeHub.statInFlight', { defaultValue: 'Batches in flight' })}
              value={loading ? '…' : String(stats.inFlight)}
              hint={t('financeHub.statInFlightHint', { defaultValue: 'Prepared or submitted' })}
            />
            <StatCard
              icon={CheckCircle2}
              tone="success"
              label={t('financeHub.statCompleted', { defaultValue: 'Completed' })}
              value={loading ? '…' : String(stats.completed)}
            />
            <StatCard
              icon={XCircle}
              tone="danger"
              label={t('financeHub.statFailed', { defaultValue: 'Failed' })}
              value={loading ? '…' : String(stats.failed)}
            />
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('financeHub.colPurpose', { defaultValue: 'Purpose' })}</TableHead>
                    <TableHead>{t('financeHub.colStatus', { defaultValue: 'Status' })}</TableHead>
                    <TableHead>{t('financeHub.colConnector', { defaultValue: 'Connector' })}</TableHead>
                    <TableHead className="text-right">{t('financeHub.colItems', { defaultValue: 'Items' })}</TableHead>
                    <TableHead className="text-right">{t('financeHub.colTotal', { defaultValue: 'Total' })}</TableHead>
                    <TableHead>{t('financeHub.colPrepared', { defaultValue: 'Prepared' })}</TableHead>
                    <TableHead>{t('financeHub.colCompleted', { defaultValue: 'Completed' })}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading &&
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={8}>
                          <Skeleton className="h-6 w-full" />
                        </TableCell>
                      </TableRow>
                    ))}

                  {!loading && batches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                        {t('financeHub.empty', {
                          defaultValue: 'No payment batches yet — they appear once a payroll run, advance or settlement is paid out.',
                        })}
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    batches.map((b) => (
                      <TableRow
                        key={b.id}
                        className="cursor-pointer"
                        onClick={() => openBatch(b)}
                      >
                        <TableCell className="font-medium text-charcoal">{purposeLabel(b.purpose)}</TableCell>
                        <TableCell>
                          <StatusChip meta={DISBURSEMENT_BATCH_META[b.status]} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">{b.connector || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums text-charcoal">{b.itemCount}</TableCell>
                        <TableCell className="text-right tabular-nums text-charcoal">
                          {formatMoney(b.totalAmount, b.currency ?? 'TZS')}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {b.preparedAt ? formatDate(b.preparedAt) : '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {b.completedAt ? formatDate(b.completedAt) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              openBatch(b);
                            }}
                          >
                            {t('financeHub.view', { defaultValue: 'View' })}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Reconciliation: the existing queue (approvers only) ----------- */}
        {canApprovePayroll && (
          <TabsContent value="reconciliation">
            <ReconciliationQueue onCountChange={onReconCountChange} />
          </TabsContent>
        )}

        {/* --- Filings: the existing statutory register ----------------------- */}
        <TabsContent value="filings">
          <FilingsRegister onOverdueChange={onOverdueChange} initialPeriod={filingsInitialPeriod} />
        </TabsContent>

        {/* --- Accounting: the QuickBooks connection + account mapping -------- */}
        {accountingAvailable && canReadAccounting && (
          <TabsContent value="accounting">
            <AccountingPanel canMap={canMapAccounting} canConnect={canConnectAccounting} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default FinanceHubPage;
